import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import QuickCapture from "@/components/QuickCapture";
import {
  LayoutDashboard, ClipboardList, ClipboardCheck, Users, ShoppingBag, Wrench, Bell, Settings, Star, Sparkles,
  LogOut, ExternalLink, Menu, X, ChevronRight,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/jobs", label: "Jobs", icon: ClipboardList },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/shop", label: "Shop", icon: ShoppingBag },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/checklist-templates", label: "Checklists", icon: ClipboardCheck },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/interest", label: "Waitlists", icon: Sparkles },
  { to: "/admin/notifications", label: "Inbox", icon: Bell, isNotifications: true },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const AdminLayout = ({ children, title, eyebrow, actions }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get("/admin/notifications", { params: { unread_only: false } });
        if (!cancelled) setUnreadCount(res.data?.unread_count || 0);
      } catch (err) {
        if (err.response?.status === 401) {
          setAdminToken(null);
          navigate("/admin/login");
        }
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [navigate]);

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-spark-teal-night text-slate-100 flex" data-testid="admin-layout">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/5 bg-spark-teal-night sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-3 px-6 py-5 border-b border-white/5">
          <img src={LOGO_URL} alt="" className="size-10 rounded-full ring-2 ring-spark-yellow" />
          <div className="leading-tight">
            <p className="font-display uppercase font-bold text-[15px] text-white">Spark</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-spark-yellow">Workshop OS</p>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-spark-teal text-white shadow-[inset_0_0_0_1px_rgba(255,217,61,0.3)]"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
                data-testid={`adminnav-${item.label.toLowerCase()}`}
              >
                <Icon className="size-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.isNotifications && unreadCount > 0 && (
                  <span className="bg-spark-yellow text-spark-ink text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-1">
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-400 hover:text-white hover:bg-white/5"
          >
            <ExternalLink className="size-3.5" /> View public site
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-400 hover:text-spark-yellow hover:bg-white/5"
            data-testid="admin-logout"
          >
            <LogOut className="size-3.5" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <aside className="relative w-72 bg-spark-teal-night border-r border-white/5 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="" className="size-8 rounded-full ring-2 ring-spark-yellow" />
                <span className="font-display uppercase font-bold text-sm text-white">Workshop OS</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white"><X className="size-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1">
              {NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                        isActive ? "bg-spark-teal text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                      }`
                    }
                  >
                    <Icon className="size-4" />
                    <span className="flex-1">{item.label}</span>
                    {item.isNotifications && unreadCount > 0 && (
                      <span className="bg-spark-yellow text-spark-ink text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount > 99 ? "99+" : unreadCount}</span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
            <button onClick={logout} className="m-3 flex items-center gap-3 px-3 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-400 hover:text-spark-yellow border border-white/5">
              <LogOut className="size-3.5" /> Log out
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 bg-gradient-to-b from-[#063338] via-[#063338] to-[#04222a]">
        {/* Topbar (mobile + page header) */}
        <header className="sticky top-0 z-30 backdrop-blur bg-spark-teal-night/85 border-b border-white/5">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-10 py-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 text-slate-300 hover:text-white" aria-label="Open menu">
              <Menu className="size-5" />
            </button>
            <div className="min-w-0 flex-1">
              {eyebrow && <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">{eyebrow}</p>}
              <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold text-white leading-tight truncate">
                {title}
              </h1>
            </div>
            {actions && <div className="hidden sm:flex items-center gap-2">{actions}</div>}
          </div>
          {actions && (
            <div className="sm:hidden px-4 pb-3 flex flex-wrap items-center gap-2">{actions}</div>
          )}
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1400px]">
          {children}
        </main>
      </div>
      <QuickCapture />
    </div>
  );
};

export default AdminLayout;

export const AdminBreadcrumb = ({ items }) => (
  <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4" aria-label="Breadcrumb">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="size-3" />}
        {it.to ? <Link to={it.to} className="hover:text-white">{it.label}</Link> : <span className="text-slate-300">{it.label}</span>}
      </span>
    ))}
  </nav>
);
